"use client";

import { auth } from '@/lib/firebase';
import { User, createUserWithEmailAndPassword, onAuthStateChanged, sendPasswordResetEmail, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { useRouter } from 'next/navigation';
import { ReactNode, createContext, useContext, useEffect, useState } from 'react';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    signIn: (email: string, pass: string) => Promise<void>;
    signUp: (email: string, pass: string) => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    logout: () => Promise<void>;
    openLoginModal: () => void;
    closeLoginModal: () => void;
    isLoginModalOpen: boolean;
    refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

import LoginModal from '@/components/LoginModal';

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const router = useRouter();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setUser(user);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const signIn = async (email: string, pass: string) => {
        await signInWithEmailAndPassword(auth, email, pass);
    };

    const signUp = async (email: string, pass: string) => {
        await createUserWithEmailAndPassword(auth, email, pass);
    };

    const resetPassword = async (email: string) => {
        await sendPasswordResetEmail(auth, email);
    };

    const logout = async () => {
        await signOut(auth);
        router.push("/ai-validator");
    };

    const openLoginModal = () => setIsLoginModalOpen(true);
    const closeLoginModal = () => setIsLoginModalOpen(false);

    const refreshUser = async () => {
        if (auth.currentUser) {
            await auth.currentUser.reload();
            // Create a clone that preserves the prototype (methods like getIdToken)
            // spreading ({...user}) breaks the prototype chain
            const currentUser = auth.currentUser;
            const userClone = Object.assign(Object.create(Object.getPrototypeOf(currentUser)), currentUser);
            setUser(userClone);
        }
    };

    return (
        <AuthContext.Provider value={{ user, loading, signIn, signUp, resetPassword, logout, openLoginModal, closeLoginModal, isLoginModalOpen, refreshUser }}>
            {children}
            <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} />
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        return {
            user: null,
            loading: false,
            signIn: async () => {},
            signUp: async () => {},
            resetPassword: async () => {},
            logout: async () => {},
            openLoginModal: () => {},
            closeLoginModal: () => {},
            isLoginModalOpen: false,
            refreshUser: async () => {},
        };
    }
    return context;
}
